import { SessionCustomerAddress } from "../session/SessionCustomerAddress";

export class Customer {
    static readonly NUM_ELEMENTS_INSIDE_LAST_ORDERS = 20;

    cognitoId: string;

    squarePaymentsCustomerId: string | null;
    squarePaymentsThirdPartyCustomerInfo:{accessTokenAccount:string, customerId:string}[];
    squareSavedCards: SquarePaymentsSavedCard[];

    lastOrdersIds: string[];

    savedAddresses: SessionCustomerAddress[];
    lastAddressUsed: SessionCustomerAddress | undefined;

    rewardPoints:{
        restaurantIdentifier:string,
        numRewardPoints:number
    }[]

    constructor(cognitoId: string, squarePaymentsCustomerId: string | null, squarePaymentsThirdPartyCustomerId:{accessTokenAccount:string, customerId:string}[], squareSavedCards: SquarePaymentsSavedCard[], lastOrdersIds: string[], 
                savedAddresses: SessionCustomerAddress[], lastAddressUsed: SessionCustomerAddress | undefined,
                rewardPoints:{ restaurantIdentifier:string, numRewardPoints:number }[]) {
        this.cognitoId = cognitoId;
        this.squarePaymentsCustomerId = squarePaymentsCustomerId;
        this.squarePaymentsThirdPartyCustomerInfo = squarePaymentsThirdPartyCustomerId;
        this.squareSavedCards = squareSavedCards==null?[]:squareSavedCards;
        this.lastOrdersIds = lastOrdersIds==null?[]:lastOrdersIds;
        this.savedAddresses = savedAddresses==null?[]:savedAddresses;
        this.lastAddressUsed = lastAddressUsed;
        this.rewardPoints = rewardPoints;
    }

    /** Generates a new SessionCustomerAddress. Either returns a full object or "undefined" */
    static newFromAws(awsCustomerData: any): Customer | undefined {
        //Cards
        let squareSavedCardList: SquarePaymentsSavedCard[] = [];
        if (awsCustomerData.squareSavedCards != null) {
            awsCustomerData.squareSavedCards.forEach((savedCard: any) => {
                squareSavedCardList.push(SquarePaymentsSavedCard.newFromAws(savedCard) as SquarePaymentsSavedCard)
            });
        }
        //Addresses
        let savedAddressesList: SessionCustomerAddress[] = [];
        if (awsCustomerData.savedAddresses != null) {
            awsCustomerData.savedAddresses.forEach((address: any) => {
                savedAddressesList.push(SessionCustomerAddress.newFromAws(address) as SessionCustomerAddress)
            });
        }
        //Reward points
        let rewardPointsList:{ restaurantIdentifier:string, numRewardPoints:number }[] = [];
        if (awsCustomerData.rewardPoints != null) {
            awsCustomerData.rewardPoints.forEach((rp: any) => {
                rewardPointsList.push({ restaurantIdentifier:rp.restaurantIdentifier, numRewardPoints:rp.numRewardPoints })
            });
        }


        if (awsCustomerData != null) {
            return new Customer(
                awsCustomerData.cognitoId,
                awsCustomerData.squarePaymentsCustomerId,
                awsCustomerData.squarePaymentsThirdPartyCustomerInfo,
                squareSavedCardList,
                awsCustomerData.lastOrdersIds,
                savedAddressesList,
                SessionCustomerAddress.newFromAws(awsCustomerData.lastAddressUsed),
                rewardPointsList
            );
        } else {
            return undefined;
        }
    }

    static generateNew(cognitoId:any){
        return new Customer(cognitoId, null, [], [],[],[],undefined,[]).toAwsObject();
    }

    /** Converts this object into a format that can be saved on our database (DynamoDB) */
    toAwsObject() {
        
        let customerAddressObj: any = {};

        //cognitoId
        customerAddressObj["cognitoId"] = this.cognitoId;
        //square ID
        if (this.squarePaymentsCustomerId != null) {
            customerAddressObj["squarePaymentsCustomerId"] = this.squarePaymentsCustomerId
        }
        //Third party square ID
        if(this.squarePaymentsThirdPartyCustomerInfo!=null){
             customerAddressObj["squarePaymentsThirdPartyCustomerInfo"] = this.squarePaymentsThirdPartyCustomerInfo;
        }
        //saved cards list
        const awsSavedCardsList: any[] = [];
        this.squareSavedCards.forEach((savedCard) => {
            awsSavedCardsList.push(savedCard.toAwsObject())
        })
        customerAddressObj["squareSavedCards"] = awsSavedCardsList;
        //last orders Ids 
        if(this.lastOrdersIds!=null){
            customerAddressObj["lastOrdersIds"] = this.lastOrdersIds;
        }
        //saved address list
        const awsSavedAddressesList: any[] = [];
        this.savedAddresses.forEach((savedAddress) => {
            awsSavedAddressesList.push(savedAddress.toAwsObject())
        })
        customerAddressObj["savedAddresses"] = awsSavedCardsList;
        //last address used
        if (this.lastAddressUsed != null) {
            customerAddressObj["lastAddressUsed"] = this.lastAddressUsed.toAwsObject();
        }
        //reward points list
        const rewardPointsList: any[] = [];
        this.rewardPoints.forEach((rp) => {
            rewardPointsList.push({
                "restaurantIdentifier":rp.restaurantIdentifier,
                "numRewardPoints":rp.numRewardPoints
            })
        })
        customerAddressObj["rewardPoints"] = rewardPointsList;


        return customerAddressObj;
    }
}

export class SquarePaymentsSavedCard {
    id: string;
    cardBrand: string;
    last4Digits: string;
    expirityMonth: number;
    expirityYear: number;
    isDefaultCreditCard:boolean;

    linkedSquareAccount:string|null;

    /**
     * Contains information about a user credit card saved on SQUARE PAYMENTS
     * @param id ID, given by square, for this credit card
     * @param cardBrand Brand of the credit card (visa, master card...)
     * @param last4Digits should be obvious
     * @param expirityMonth should be obvious
     * @param expirityYear should be obvious
     * @param isDefaultCreditCard if TRUE, this is the credit card that will be selected by default when the user tries to pay online
     * @param linkedSquareAccount defines the square account linked to this credit card
     */
    constructor(id: string, cardBrand: string, last4Digits: string, expirityMonth: number, expirityYear: number, isDefaultCreditCard:boolean, 
                linkedSquareAccount:string|null) {
        this.id = id;
        this.cardBrand = cardBrand;
        this.last4Digits = last4Digits;
        this.expirityMonth = expirityMonth;
        this.expirityYear = expirityYear;
        this.isDefaultCreditCard = isDefaultCreditCard;
        this.linkedSquareAccount = linkedSquareAccount;
    }

    /** Generates a new SessionCustomerAddress. Either returns a full object or "undefined" */
    static newFromAws(awsSquareCardData: any): SquarePaymentsSavedCard | undefined {
        if (awsSquareCardData != null) {
            return new SquarePaymentsSavedCard(
                awsSquareCardData.id,
                awsSquareCardData.cardBrand,
                awsSquareCardData.last4Digits,
                awsSquareCardData.expirityMonth,
                awsSquareCardData.expirityYear,
                awsSquareCardData.isDefaultCreditCard,
                awsSquareCardData.linkedSquareAccount
            );
        } else {
            return undefined;
        }
    }

    /**
     * Generates a new payment card object with the data received from the square payment form used
     * to gather the credit card information
     * @param squareDataObj 
     */
    static newFromSquareData(squareDataObj:any){
        if (squareDataObj != null) {
            return new SquarePaymentsSavedCard(
                squareDataObj.id,
                squareDataObj.card_brand,
                squareDataObj.last_4,
                squareDataObj.exp_month,
                squareDataObj.exp_year,
                true,
                squareDataObj.linkedSquareAccount
            );
        } else {
            return undefined;
        }
    }

    /** Converts this object into a format that can be saved on our database (DynamoDB) */
    toAwsObject() {
        const squareCardObj = {
            "id": this.id,
            "cardBrand": this.cardBrand,
            "last4Digits": this.last4Digits,
            "expirityMonth": this.expirityMonth,
            "expirityYear": this.expirityYear,
            "isDefaultCreditCard": this.isDefaultCreditCard,
            "linkedSquareAccount":this.linkedSquareAccount
        }
        return squareCardObj;
    }

    static getCardBrandInfo(cardBrand: string): { css: string, title: string } {
        switch (cardBrand) {
          case "VISA":
            return { css: "fab fa-cc-visa", title: "Visa" }
          case "JCB":
            return { css: "fab fa-cc-jcb", title: "JCB" }
          case "DISCOVER":
            return { css: "fab fa-cc-discover", title: "Discover" }
          case "MASTERCARD":
            return { css: "fab fa-cc-mastercard", title: "Mastercard" }
          case "DISCOVER_DINERS":
            return { css: "fab fa-cc-diners-club", title: "Diners Club" }
          case 'AMERICAN_EXPRESS':
            return { css: "fab fa-cc-amex", title: "American Express" }
          default:
            return { css: "far fa-credit-card", title: "Credit Card" }
        }
      }
}